import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { outreachService } from "../services/outreach-service";
import { 
    CreateCampaignRequest, 
    CreatePitchRequest, 
    UpdatePitchStatusRequest, 
    UpdatePitchDraftRequest, 
    SendPitchEmailRequest 
} from "../types/outreach";
import { toast } from "sonner";

export const OUTREACH_KEYS = {
  allCampaigns: ['campaigns'],
  campaignPitches: (campaignId: string) => ['pitches', campaignId],
  pitchEmails: (pitchId: string) => ['pitch-emails', pitchId],
};

// Campaigns
export function useGetCampaigns() {
  return useQuery({
    queryKey: OUTREACH_KEYS.allCampaigns,
    queryFn: outreachService.getCampaigns,
  });
}

// Emails
export function useGetPitchEmails(pitchId: string) {
  return useQuery({
    queryKey: OUTREACH_KEYS.pitchEmails(pitchId),
    queryFn: () => outreachService.getPitchEmails(pitchId),
    enabled: !!pitchId
  });
}

export function useSendPitchEmail() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ pitchId, data }: { pitchId: string, data: SendPitchEmailRequest }) => 
        outreachService.sendPitchEmail(pitchId, data),
    onSuccess: (_data, { pitchId }) => {
        queryClient.invalidateQueries({ queryKey: OUTREACH_KEYS.pitchEmails(pitchId) });
        queryClient.invalidateQueries({ queryKey: ['pitches'] });
        toast.success("Email sent!");
    },
    onError: (error) => {
        console.error(error);
        toast.error("Failed to send email");
    }
  });
}

export function useUpdatePitchDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ pitchId, data }: { pitchId: string, data: UpdatePitchDraftRequest }) => 
        outreachService.updatePitchDraft(pitchId, data),
    onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['pitches'] });
        toast.success("Draft saved");
    },
    onError: () => {
        toast.error("Failed to save draft");
    }
  });
}

export function useCreateCampaign() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateCampaignRequest) => outreachService.createCampaign(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: OUTREACH_KEYS.allCampaigns });
      toast.success("Campaign created successfully");
    },
    onError: (error) => {
      console.error(error);
      toast.error("Failed to create campaign");
    }
  });
}

// Pitches
export function useGetCampaignPitches(campaignId: string) {
  return useQuery({
    queryKey: OUTREACH_KEYS.campaignPitches(campaignId),
    queryFn: () => outreachService.getCampaignPitches(campaignId),
    enabled: !!campaignId
  });
}

export function useCreatePitch() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data: CreatePitchRequest) => outreachService.createPitch(data),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: OUTREACH_KEYS.campaignPitches(variables.campaignId) });
             // Also invalidate campaigns to update counts if we had them
            queryClient.invalidateQueries({ queryKey: OUTREACH_KEYS.allCampaigns });
            toast.success("Added to campaign");
        },
        onError: () => {
             toast.error("Failed to add to campaign");
        }
    });
}

export function useUpdatePitchStatus() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ pitchId, data }: { pitchId: string, data: UpdatePitchStatusRequest }) => 
            outreachService.updatePitchStatus(pitchId, data),
        onSuccess: () => {
             // We need to know the campaignId to invalidate properly, but it's not in the variables.
             // We can invalidate all pitches for now or be more granular if we passed campaignId.
             // Simple approach: invalidate 'pitches' root key if we used it, but we used ['pitches', id].
             // Better: Let's assume we invalidate all pitches queries.
             queryClient.invalidateQueries({ queryKey: ['pitches'] });
             toast.success("Status updated");
        },
        onError: () => {
            toast.error("Failed to update status");
        }
    });
}
